import { Printer } from "lucide-react";
import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
	clientUnavailableReason,
	IMMEDIATE_DIALOG_NOTE,
	PLAN_DIALOG_NOTE,
	portKindLabel,
} from "./printer-labels";
import type { PlanAction, PlanNames } from "./printer-plan";
import type { PrinterDevice, PrinterItem } from "./printers-api";

/**
 * `clientDeviceUuid` — stanowisko zaznaczone na start (z „Drukarka domyślna…”);
 * gdy nie może przyjąć udziału, okno wybiera pierwsze dostępne.
 */
export type ShareTarget = { host: PrinterDevice; printer: PrinterItem; clientDeviceUuid?: string };

/**
 * „Udostępnij drukarkę”: drukarka na kablu komputera A → wybrane stanowisko B
 * tej samej apteki. Udostępnienie kopiuje dostęp, nie przenosi drukarki — A
 * drukuje dalej. Okno dopisuje pozycję do planu; postęp kroków pokazuje
 * wodospad wykonania planu.
 */
export function ShareDialog({
	target,
	devices,
	minAgentVersion,
	onPlan,
	onClose,
	immediate = false,
}: {
	target: ShareTarget | null;
	devices: PrinterDevice[];
	minAgentVersion: string;
	onPlan: (action: PlanAction, names: PlanNames) => void;
	onClose: () => void;
	/** Panel helpdesku: „Wykonaj” startuje operację od razu, bez planu. */
	immediate?: boolean;
}) {
	return (
		<Dialog open={target != null} onOpenChange={(next) => (next ? undefined : onClose())}>
			<DialogContent className="max-w-lg">
				{target ? (
					<ShareDialogBody
						// Nowa drukarka albo stanowisko = świeży formularz.
						key={`${target.host.deviceUuid}:${target.printer.name}:${target.clientDeviceUuid ?? ""}`}
						target={target}
						devices={devices}
						minAgentVersion={minAgentVersion}
						onPlan={onPlan}
						onClose={onClose}
						immediate={immediate}
					/>
				) : null}
			</DialogContent>
		</Dialog>
	);
}

function ShareDialogBody({
	target,
	devices,
	minAgentVersion,
	onPlan,
	onClose,
	immediate,
}: {
	target: ShareTarget;
	devices: PrinterDevice[];
	minAgentVersion: string;
	onPlan: (action: PlanAction, names: PlanNames) => void;
	onClose: () => void;
	immediate: boolean;
}) {
	const { host, printer } = target;
	const groupId = useId();
	const usingUuids = new Set(printer.usedBy.map((u) => u.deviceUuid));
	const candidates = devices
		.filter((d) => d.deviceUuid !== host.deviceUuid)
		.map((device) => ({
			device,
			reason: clientUnavailableReason(device, minAgentVersion, usingUuids.has(device.deviceUuid)),
		}));
	const available = candidates.filter((c) => c.reason == null).map((c) => c.device.deviceUuid);
	const preselected =
		target.clientDeviceUuid && available.includes(target.clientDeviceUuid)
			? target.clientDeviceUuid
			: null;
	const [clientUuid, setClientUuid] = useState<string | null>(preselected ?? available[0] ?? null);
	const [setDefault, setSetDefault] = useState(true);
	const selected = candidates.find((c) => c.device.deviceUuid === clientUuid && c.reason == null);

	const submit = () => {
		if (!selected) return;
		onPlan(
			{
				kind: "share",
				input: {
					hostDeviceUuid: host.deviceUuid,
					printerName: printer.name,
					clientDeviceUuid: selected.device.deviceUuid,
					setDefault,
				},
			},
			{ host: host.displayName, client: selected.device.displayName, printer: printer.name },
		);
		onClose();
	};

	return (
		<>
			<DialogHeader>
				<DialogTitle>Udostępnij drukarkę</DialogTitle>
				<DialogDescription>
					Stanowisko dostanie tę drukarkę przez sieć lokalną apteki — {host.displayName} drukuje na
					niej dalej.
				</DialogDescription>
			</DialogHeader>
			<DialogBody className="space-y-4">
				<div className="flex items-start gap-2 rounded-md border bg-muted/40 p-2 text-sm">
					<Printer className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
					<div className="min-w-0">
						<p className="font-medium break-words">{printer.name}</p>
						<p className="text-xs text-muted-foreground">
							Podłączona ({portKindLabel(printer.portKind)}) do komputera{" "}
							<span className="font-medium text-foreground">{host.displayName}</span>
						</p>
					</div>
				</div>

				<fieldset className="space-y-1.5">
					<legend className="mb-1 text-sm font-medium">
						Które stanowisko ma na niej drukować?
					</legend>
					{candidates.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							W tej aptece nie ma innych komputerów z agentem Farmastera.
						</p>
					) : (
						candidates.map(({ device, reason }) => {
							const inputId = `${groupId}-${device.deviceUuid}`;
							return (
								<label
									key={device.deviceUuid}
									htmlFor={inputId}
									className={cn(
										"flex min-h-9 items-center gap-2 rounded-md border px-2 py-1.5 text-sm",
										reason
											? "cursor-not-allowed bg-muted/40 text-muted-foreground"
											: "cursor-pointer hover:bg-muted/50",
										clientUuid === device.deviceUuid && !reason && "border-primary bg-primary/5",
									)}
								>
									<input
										id={inputId}
										type="radio"
										name={groupId}
										value={device.deviceUuid}
										checked={clientUuid === device.deviceUuid && !reason}
										disabled={reason != null}
										onChange={() => setClientUuid(device.deviceUuid)}
										className="size-4 shrink-0 accent-primary"
									/>
									<span className="min-w-0 flex-1 truncate">{device.displayName}</span>
									{reason ? <span className="shrink-0 text-xs">{reason}</span> : null}
								</label>
							);
						})
					)}
				</fieldset>

				<label className="flex items-center gap-2 text-sm">
					<input
						type="checkbox"
						checked={setDefault}
						onChange={(e) => setSetDefault(e.target.checked)}
						className="size-4 accent-primary"
					/>
					Ustaw jako domyślną drukarkę na stanowisku
				</label>
				<p className="text-xs text-muted-foreground">
					{immediate ? IMMEDIATE_DIALOG_NOTE : PLAN_DIALOG_NOTE}
				</p>
			</DialogBody>
			<DialogFooter>
				<Button variant="outline" size="sm" onClick={onClose}>
					Anuluj
				</Button>
				<Button size="sm" onClick={submit} disabled={!selected}>
					{immediate ? "Wykonaj" : "Dodaj do planu"}
				</Button>
			</DialogFooter>
		</>
	);
}
